import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Rating,
  Collapse,
  IconButton,
  Divider,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import ComplianceTrendIndicator from "../../../../../components/ComplianceTrendIndicator";

const ComplianceCard = ({ compliance, uid, organizationId }) => {
  const [expanded, setExpanded] = useState(false);

  if (!compliance || compliance.overallRating === 0) {
    return (
      <Card
        sx={(theme) => ({
          mb: 3,
          borderRadius: theme.spacing(2),
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        })}
      >
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Compliance Score
          </Typography>
          <Typography color="text.secondary">
            No compliance data available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const {
    overallRating,
    overallScore,
    categories,
    summary,
    excludedCategories,
    tracking,
  } = compliance;

  const formatCategoryName = (key) => {
    const names = {
      exercise: "Exercise",
      nutrition: "Nutrition",
      dailyCheckIn: "Daily Check-ins",
      weeklyQuestions: "Weekly Questions",
      supplements: "Supplements",
    };
    return names[key] || key;
  };

  const getChipLabel = (key, category) => {
    const baseName = formatCategoryName(key);
    const score = Math.round(category.score * 100);

    // Check if this category is being tracked
    if (tracking && !tracking[key]) {
      return `${baseName}: Excluded (no data)`;
    }

    // Special handling for supplements
    if (key === "supplements" && category.details) {
      if (typeof category.details === "string") {
        // Old format - just show the string
        return `${baseName}: ${category.details}`;
      } else if (category.details.prescribed === 0) {
        return `${baseName}: N/A (none prescribed)`;
      } else if (!category.details.hasLogging) {
        return `${baseName}: 0% (not tracking)`;
      }
    }

    return `${baseName}: ${score}%`;
  };

  const getChipColor = (key, category) => {
    // If category is excluded from tracking, show as disabled
    if (tracking && !tracking[key]) {
      return "default";
    }

    // Otherwise use score-based coloring
    if (category.score >= 0.8) return "success";
    if (category.score >= 0.6) return "warning";
    return "error";
  };

  return (
    <Card
      sx={(theme) => ({
        mb: 3,
        borderRadius: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      })}
    >
      <CardContent>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="h6">Compliance Score</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Rating value={overallRating} readOnly />
            <Typography variant="h6" color="primary">
              {overallScore}%
            </Typography>
            {/* Add compliance trend indicator */}
            {uid && organizationId && (
              <ComplianceTrendIndicator
                uid={uid}
                organizationId={organizationId}
                showDetails={false}
              />
            )}
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" mb={3}>
          {summary}
        </Typography>

        <Box display="flex" flexWrap="wrap" gap={1}>
          {Object.entries(categories).map(([key, category]) => (
            <Chip
              key={key}
              label={getChipLabel(key, category)}
              color={getChipColor(key, category)}
              variant="outlined"
              size="small"
            />
          ))}
        </Box>

        {/* Show information about excluded categories if any */}
        {excludedCategories && excludedCategories.length > 0 && (
          <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
            <Typography variant="body2" color="text.secondary">
              <strong>Note:</strong>{" "}
              {excludedCategories.length === 1
                ? "This category is"
                : "These categories are"}{" "}
              excluded from the overall compliance score because no tracking
              data was found:{" "}
              {excludedCategories
                .map((cat) => formatCategoryName(cat))
                .join(", ")}
              .
            </Typography>
          </Box>
        )}

        {/* Detailed breakdown - could be expanded in a collapsible section */}
        <Box
          mt={2}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="body2" color="text.secondary">
            Report Period: {compliance.reportPeriod?.totalDays} days
          </Typography>
          <IconButton
            onClick={() => setExpanded(!expanded)}
            size="small"
            sx={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s",
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Detailed Breakdown
          </Typography>

          {/* Nutrition Details */}
          {categories.nutrition && categories.nutrition.details && (
            <Box mb={2}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Nutrition Compliance
              </Typography>
              <Box ml={2}>
                {categories.nutrition.details.goalAdherenceRate !==
                  undefined && (
                  <Typography variant="body2" color="text.secondary">
                    • Goal Adherence:{" "}
                    {categories.nutrition.details.goalAdherenceRate}% (
                    {categories.nutrition.details.goalAdherenceDays} days with
                    goals)
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  • Macro Tracking:{" "}
                  {categories.nutrition.details.macroTrackingRate}% (
                  {categories.nutrition.details.daysWithMacros}/
                  {categories.nutrition.details.daysLogged} days)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Water Tracking:{" "}
                  {categories.nutrition.details.waterTrackingRate}% (
                  {categories.nutrition.details.daysWithWater}/
                  {categories.nutrition.details.daysLogged} days)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Consistency: {categories.nutrition.details.consistencyRate}%
                </Typography>
              </Box>
            </Box>
          )}

          {/* Exercise Details */}
          {categories.exercise &&
            categories.exercise.details &&
            typeof categories.exercise.details === "object" && (
              <Box mb={2}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Exercise Compliance
                </Typography>
                <Box ml={2}>
                  <Typography variant="body2" color="text.secondary">
                    • Workout Completion:{" "}
                    {categories.exercise.details.workoutCompletionRate}% (
                    {categories.exercise.details.workoutsCompleted}/
                    {categories.exercise.details.workoutsLogged} workouts)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Exercise Completion:{" "}
                    {categories.exercise.details.exerciseCompletionRate}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Set Quality: {categories.exercise.details.setQualityRate}%
                  </Typography>
                </Box>
              </Box>
            )}

          {/* Daily Check-in Details */}
          {categories.dailyCheckIn &&
            categories.dailyCheckIn.details &&
            typeof categories.dailyCheckIn.details === "object" && (
              <Box mb={2}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Daily Check-in Compliance
                </Typography>
                <Box ml={2}>
                  <Typography variant="body2" color="text.secondary">
                    • Completion Rate:{" "}
                    {categories.dailyCheckIn.details.completionRate}% (
                    {categories.dailyCheckIn.details.completedFields}/
                    {categories.dailyCheckIn.details.totalFields} fields)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Response Quality:{" "}
                    {categories.dailyCheckIn.details.averageQuality}%
                  </Typography>
                </Box>
              </Box>
            )}

          {/* Weekly Questions Details */}
          {categories.weeklyQuestions &&
            categories.weeklyQuestions.details &&
            typeof categories.weeklyQuestions.details === "object" && (
              <Box mb={2}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Weekly Questions Compliance
                </Typography>
                <Box ml={2}>
                  <Typography variant="body2" color="text.secondary">
                    • Completion Rate:{" "}
                    {categories.weeklyQuestions.details.completionRate}% (
                    {categories.weeklyQuestions.details.answered}/
                    {categories.weeklyQuestions.details.totalRequired} required)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Response Quality:{" "}
                    {categories.weeklyQuestions.details.averageQuality}%
                  </Typography>
                </Box>
              </Box>
            )}

          {/* Supplement Details */}
          {categories.supplements && categories.supplements.details && (
            <Box mb={1}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Supplement Compliance
              </Typography>
              <Box ml={2}>
                <Typography variant="body2" color="text.secondary">
                  • Status: {categories.supplements.details.status}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Prescribed: {categories.supplements.details.prescribed}{" "}
                  supplements
                </Typography>
                {categories.supplements.details.hasLogging ? (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      • Tracking Days:{" "}
                      {categories.supplements.details.trackingDays}
                    </Typography>
                    {categories.supplements.details.trackingRate !==
                      undefined && (
                      <Typography variant="body2" color="text.secondary">
                        • Tracking Rate:{" "}
                        {categories.supplements.details.trackingRate}%
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    • No supplement tracking logged
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default ComplianceCard;
